import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Upload, message, Avatar, Card, Row, Col, Spin, notification } from 'antd';
import { CameraOutlined, UserOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiHandle } from '../../config/apiHandle';
import { check_auth } from '../../services/authentication';

const { Item } = Form;

const Settings = () => {
    
    const dispatch = useDispatch()
    const { user } = useSelector((state) => state.userAuth);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false); // Loading state for image upload
    const [avatarUrl, setAvatarUrl] = useState(''); // Stores the Cloudinary URL
    const [previewImage, setPreviewImage] = useState('');

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phonenumber: user.phonenumber || '',
            });
            if (user.avatar) {
                setPreviewImage(user.avatar);
                setAvatarUrl(user.avatar);
            }
        }
    }, [user, form]);

    const handleBeforeUpload = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            notification.error('You can only upload image files!');
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleImageChange = async (info) => {
        if (info.file.status === 'uploading') {
            setImageLoading(true); // Show loading while uploading
        }

        if (info.file.status === 'done') {
            try {
                // Upload image to Cloudinary
                const formData = new FormData();
                formData.append('binary_file', info.file.originFileObj);

                const response = await apiHandle.post('upload-files', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                const imageUrl = response.data.url; // Get the Cloudinary URL
                setAvatarUrl(imageUrl); // Save the URL for profile update
                setPreviewImage(imageUrl); // Update preview
                notification.success('Image uploaded successfully!');
            } catch (error) {
                notification.error('Failed to upload image');
            } finally {
                setImageLoading(false); // Hide loading
            }
        }

        if (info.file.status === 'error') {
            notification.error('Image upload failed');
            setImageLoading(false);
        }
    };

    const onFinish = async (values) => {
        if (!avatarUrl) {
            notification.error('Please upload an image first');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                first_name: values.first_name,
                last_name: values.last_name,
                phonenumber: values.phonenumber,
                avatar: avatarUrl, // Use the Cloudinary URL
            };

            await apiHandle.post('vendor/update-profile', payload);

            notification.success('Profile updated successfully!');
            dispatch(check_auth())
        } catch (error) {
            notification.error(error.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const isProfileComplete = () => {
        return (
            user?.first_name &&
            user?.last_name &&
            user?.phonenumber &&
            user?.avatar
        );
    };

    return (
        <div className="">
            <Card className="max-w-4xl mx-auto shadow-lg rounded-xl overflow-hidden">
                {/* Profile Completion Banner */}
                {!isProfileComplete() && (
                    <div className="bg-orange-50 p-6 mb-8 rounded-t-lg border-b border-orange-200">
                        <div className="flex items-center gap-4">
                            <InfoCircleOutlined className="text-2xl text-orange-600" />
                            <div>
                                <h2 className="text-xl font-semibold text-orange-600">Complete Your Profile</h2>
                                <p className="text-orange-600 mt-1">
                                    Please provide your information to complete your profile
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <Row gutter={[32, 32]} className="px-6 pb-6">
                    {/* Avatar Upload Section */}
                    <Col xs={24} md={8} className="text-center">
                        <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={handleBeforeUpload}
                            onChange={handleImageChange}
                            customRequest={({ file, onSuccess }) => {
                                setTimeout(() => onSuccess("ok"), 0);
                            }}
                        >
                            <div className="relative inline-block group cursor-pointer">
                                {imageLoading ? (
                                    <Spin size="large" className="flex items-center justify-center h-40" />
                                ) : (
                                    <Avatar
                                        size={160}
                                        src={previewImage}
                                        icon={<UserOutlined className="text-4xl" />}
                                        className="border-4 border-gray-100 shadow-lg group-hover:border-blue-100 transition-colors"
                                    />
                                )}
                                <div className="absolute bottom-3 right-3 bg-white p-2 rounded-full shadow-md transform group-hover:scale-110 transition-all">
                                    <CameraOutlined className="text-xl text-gray-700" />
                                </div>
                            </div>
                        </Upload>
                        <p className="text-gray-500 mt-4 text-sm font-medium">
                            {previewImage ? 'Click to change photo' : 'Click to upload photo'}
                        </p>
                    </Col>

                    {/* Profile Form Section */}
                    <Col xs={24} md={16}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            initialValues={{ remember: true }}
                        >
                            <Row gutter={24}>
                                <Col xs={24} md={12}>
                                    <Item
                                        label="First Name"
                                        name="first_name"
                                        rules={[{ required: true, message: 'First name is required' }]}
                                    >
                                        <Input
                                            placeholder="John"
                                            size="large"
                                            className="rounded-lg border-gray-300 hover:border-blue-400"
                                        />
                                    </Item>
                                </Col>

                                <Col xs={24} md={12}>
                                    <Item
                                        label="Last Name"
                                        name="last_name"
                                        rules={[{ required: true, message: 'Last name is required' }]}
                                    >
                                        <Input
                                            placeholder="Doe"
                                            size="large"
                                            className="rounded-lg border-gray-300 hover:border-blue-400"
                                        />
                                    </Item>
                                </Col>

                                <Col xs={24}>
                                    <Item
                                        label="Email Address"
                                        name="email"
                                        rules={[
                                            { required: true, message: 'Email is required' },
                                            { type: 'email', message: 'Invalid email format' }
                                        ]}
                                    >
                                        <Input
                                            disabled
                                            size="large"
                                            className="rounded-lg bg-gray-100"
                                        />
                                    </Item>
                                </Col>

                                <Col xs={24}>
                                    <Item
                                        label="Phone Number"
                                        name="phonenumber"
                                        rules={[
                                            { required: true, message: 'Phone number is required' },
                                            { pattern: /^[0-9]{10}$/, message: 'Invalid phone number (10 digits)' }
                                        ]}
                                    >
                                        <Input
                                            placeholder="1234567890"
                                            size="large"
                                            className="rounded-lg border-gray-300 hover:border-blue-400"
                                        />
                                    </Item>
                                </Col>

                                <Col xs={24} className="mt-6">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        loading={loading}
                                        className="w-full md:w-48 h-12 rounded-lg font-semibold text-lg shadow-md hover:shadow-lg transition-all"
                                    >
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default Settings;